import React, { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { Newspaper, PlayCircle, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { toEmbedSrc } from '../../lib/videoEmbed';
import { CollapsibleSection } from './CollapsibleSection';

// Latest Intercambio Inmobiliario internal post (show_in_crm) surfaced on the
// dashboard "Novedades" section. Reads blog_posts directly (authenticated RLS
// allows reading published posts) — no new RPC.
interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  cover_image_url: string | null;
  author_name: string | null;
  published_at: string | null;
  tags: string[] | null;
  post_type: 'article' | 'vlog' | null;
  video_url: string | null;
}

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return '';
  const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  if (days < 1) return 'Hoy';
  if (days === 1) return 'Ayer';
  if (days < 30) return `Hace ${days} días`;
  return new Date(dateStr).toLocaleDateString('es-GT', { day: '2-digit', month: 'short', year: 'numeric' });
}

export const NovedadesCard: React.FC<{ theme?: 'light' | 'dark'; staggerIndex?: number }> = ({ theme = 'dark', staggerIndex = 0 }) => {
  const isLight = theme !== 'dark';
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!supabase) { setLoading(false); return; }
    let cancelled = false;
    supabase
      .from('blog_posts')
      .select('id,title,slug,excerpt,cover_image_url,author_name,published_at,tags,post_type,video_url')
      .eq('status', 'published')
      .eq('show_in_crm', true)
      .order('published_at', { ascending: false, nullsFirst: false })
      .limit(1)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (!error && data && data.length) setPost(data[0] as BlogPost);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // No empty collapsible when there's genuinely no news.
  if (loading || !post) return null;

  const isVlog = post.post_type === 'vlog' && !!post.video_url;
  const embedSrc = isVlog && post.video_url ? toEmbedSrc(post.video_url) : null;
  const openPost = () => navigate(`/novedades/${post.slug}`);

  return (
    <CollapsibleSection
      title="Novedades"
      icon={isVlog ? PlayCircle : Newspaper}
      storageKey="dash_novedades"
      theme={theme}
      staggerIndex={staggerIndex}
      badge={{ text: isVlog ? 'Nuevo video' : 'Nuevo' }}
    >
      {isVlog && embedSrc ? (
        <div>
          <div className="relative w-full rounded-xl overflow-hidden" style={{ paddingTop: '56.25%' }}>
            <iframe
              src={embedSrc}
              title={post.title}
              className="absolute inset-0 w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
          <button onClick={openPost} className="text-left mt-3 w-full group">
            <h4 className={clsx('font-bold group-hover:text-indigo-500 transition-colors', isLight ? 'text-slate-900' : 'text-white')}>{post.title}</h4>
            <p className="text-xs text-slate-500 mt-1">{post.author_name || 'Intercambio Inmobiliario'} · {timeAgo(post.published_at)}</p>
          </button>
        </div>
      ) : (
        <button onClick={openPost} className="flex flex-col sm:flex-row gap-4 group text-left w-full">
          {post.cover_image_url && (
            <div className="relative sm:w-56 shrink-0 rounded-xl overflow-hidden aspect-video">
              <img src={post.cover_image_url} alt={post.title} className="w-full h-full object-cover" loading="lazy" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            {post.tags?.length ? (
              <span className={clsx('text-[10px] font-bold px-2 py-0.5 rounded-chip uppercase', isLight ? 'bg-slate-100 text-slate-600' : 'bg-white/10 text-slate-300')}>
                {post.tags[0]}
              </span>
            ) : null}
            <h4 className={clsx('mt-2 font-bold leading-snug group-hover:text-indigo-500 transition-colors', isLight ? 'text-slate-900' : 'text-white')}>
              {post.title}
            </h4>
            {post.excerpt && <p className="text-sm text-slate-500 mt-1 line-clamp-2">{post.excerpt}</p>}
            <p className="text-xs text-indigo-500 font-semibold mt-2 flex items-center gap-1">
              Leer más <ArrowRight size={12} />
              <span className="text-slate-400 font-normal ml-1">· {timeAgo(post.published_at)}</span>
            </p>
          </div>
        </button>
      )}

      <div className={clsx('mt-4 pt-3 border-t flex justify-end', isLight ? 'border-slate-100' : 'border-white/5')}>
        <button onClick={() => navigate('/novedades')} className="text-xs font-bold text-indigo-500 hover:text-indigo-400 flex items-center gap-1">
          Ver todas las novedades <ArrowRight size={13} />
        </button>
      </div>
    </CollapsibleSection>
  );
};
